'use client';

import Link from 'next/link';
import { ArrowRight, BookOpen, Shield, Zap } from 'lucide-react';
import FadeIn from '../ui/FadeIn';

const PREVIEW_GUIDES = [
    {
        slug: 'whatsapp-privacy-update-2026-strategy',
        title: 'The 2026 Privacy Strategy',
        desc: 'How to navigate the shift to usernames and IMEI blocking while keeping your contact data accessible.',
        icon: <Shield className="w-5 h-5 text-[var(--color-md-primary)]" />,
        tag: 'Strategy Briefing'
    },
    {
        slug: 'how-to-export-whatsapp-contacts-to-excel-2026',
        title: 'Mastering Excel Exports',
        desc: 'Step-by-step framework to turn raw WhatsApp data into organized CRM-ready lead lists.',
        icon: <BookOpen className="w-5 h-5 text-[var(--color-md-secondary)]" />,
        tag: 'Tutorial'
    },
    {
        slug: 'the-missing-crm-layer-for-whatsapp-web',
        title: 'The Missing CRM Layer',
        desc: 'Why you don\'t need a $100/mo API platform to manage your WhatsApp leads professionally.',
        icon: <Shield className="w-5 h-5 text-[var(--color-md-tertiary)]" />,
        tag: 'Thought Leadership'
    }
];

export default function GuidePreview() {
    return (
        <section className="py-24 bg-[var(--color-md-surface)] border-t border-[var(--color-md-outline-variant)]">
            <div className="container mx-auto px-6">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16">
                    <FadeIn>
                        <div>
                            <div className="inline-block px-4 py-2 rounded-full bg-[var(--color-md-primary)]/10 text-[var(--color-md-primary)] text-xs font-bold uppercase tracking-widest mb-6">
                                Knowledge Hub
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-[var(--color-md-on-surface)] tracking-tighter mb-4">
                                Strategic WhatsApp <span className="text-[var(--color-md-primary)]">Intelligence</span>
                            </h2>
                            <p className="text-lg text-[var(--color-md-on-surface-variant)] leading-relaxed max-w-2xl">
                                We don't just export numbers; we build the organizational layer for your business. Explore our deep-dive guides on AEO, lead generation, and privacy.
                            </p>
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.1}>
                        <Link
                            href="/guides"
                            className="group flex items-center gap-2 text-[var(--color-md-primary)] font-bold hover:gap-4 transition-all"
                        >
                            View All Guides
                            <ArrowRight className="w-5 h-5" />
                        </Link>
                    </FadeIn>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {PREVIEW_GUIDES.map((guide, i) => (
                        <FadeIn key={guide.slug} delay={0.1 + i * 0.1}>
                            <Link
                                href={`/guides/${guide.slug}`}
                                className="group block p-8 rounded-3xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline)] hover:border-[var(--color-md-primary)] hover:shadow-cinematic-2 transition-all h-full"
                            >
                                <div className="flex items-start justify-between mb-8">
                                    <div className={`w-11 h-11 ${
                                        i === 0 ? 'm3-icon-tonal' : 
                                        i === 1 ? 'm3-icon-secondary' : 
                                        'm3-icon-tertiary'
                                    }`}>
                                        {guide.icon}
                                    </div>
                                    <span className="text-[11px] font-black uppercase tracking-widest text-[var(--color-md-outline)]">
                                        {guide.tag}
                                    </span>
                                </div>
                                <h3 className="text-xl font-black text-[var(--color-md-on-surface)] mb-4 leading-tight group-hover:text-[var(--color-md-primary)] transition-colors">
                                    {guide.title}
                                </h3>
                                <p className="text-sm text-[var(--color-md-on-surface-variant)] leading-relaxed mb-6">
                                    {guide.desc}
                                </p>
                                <div className="flex items-center gap-2 text-xs font-bold text-[var(--color-md-primary)] opacity-0 group-hover:opacity-100 transition-all">
                                    Read Guide <ArrowRight className="w-4 h-4" />
                                </div>
                            </Link>
                        </FadeIn>
                    ))}
                </div>

                {/* Machine Readable facts callout */}
                <FadeIn delay={0.5}>
                    <div className="mt-12 p-8 rounded-3xl bg-[var(--color-md-surface-variant)] border border-[var(--color-md-outline-variant)] flex flex-col md:flex-row items-center justify-between gap-8">
                        <div className="flex items-center gap-6">
                            <div className="w-12 h-12 m3-icon-tonal shrink-0 rounded-2xl">
                                <Zap className="w-6 h-6" />
                            </div>
                            <div>
                                <h4 className="font-bold text-[var(--color-md-on-surface)]">Machine-Readable Product Data</h4>
                                <p className="text-sm text-[var(--color-md-on-surface-variant)]">Factual documentation optimized for LLMs and AI Answer Engines.</p>
                            </div>
                        </div>
                        <Link
                            href="/product-facts"
                            className="px-6 py-3 rounded-full bg-[var(--color-md-surface)] border border-[var(--color-md-outline)] text-sm font-bold text-[var(--color-md-on-surface)] hover:bg-[var(--color-md-primary)] hover:text-white transition-all shadow-sm"
                        >
                            View Product Facts
                        </Link>
                    </div>
                </FadeIn>
            </div>
        </section>
    );
}
